// packages/common-ui/src/utils/analyticsRulesEngine.ts

interface SectionRule {
  id: string;
  priority: number;
  condition: (props: { title?: string; componentType?: string; layoutIndex?: number; contentItem?: any }) => boolean;
  result: {
    section: string;
    eventType: 'homeHeroBanner' | 'homeSectionBanner';
    ruleId: string;
  };
}

const HERO_BANNER = 'hero banner';
const HOME_HERO_BANNER = 'homeHeroBanner';
const HOME_SECTION_BANNER = 'homeSectionBanner';

const HOMEPAGE_RULES: SectionRule[] = [
  // Hero detection rules (highest priority)
  {
    id: 'hero-image-position',
    priority: 1,
    condition: props => props.layoutIndex === 0 && props.componentType === 'Component-CmsImage',
    result: { section: HERO_BANNER, eventType: HOME_HERO_BANNER, ruleId: 'hero-image-position' },
  },
  {
    id: 'hero-banner-position',
    priority: 2,
    condition: props =>
      !!props.componentType &&
      ['Component-CmsBanner', 'Component-CmsCarousel'].includes(props.componentType) &&
      props.layoutIndex === 0,
    result: { section: HERO_BANNER, eventType: HOME_HERO_BANNER, ruleId: 'hero-banner-position' },
  },
  {
    id: 'hero-title-keywords',
    priority: 3,
    condition: props => {
      const heroTerms = /hero|welcome/i;
      return !!props.title && heroTerms.test(props.title) && (props.layoutIndex ?? 0) <= 1;
    },
    result: { section: HERO_BANNER, eventType: HOME_HERO_BANNER, ruleId: 'hero-title-keywords' },
  },

  // Section detection rules
  {
    id: 'best-selling-keywords',
    priority: 4,
    condition: props => {
      const bestSellingTerms = /best.?sell|popular|top.?pick|recommend/i;
      return !!props.title && bestSellingTerms.test(props.title);
    },
    result: { section: 'best selling', eventType: HOME_SECTION_BANNER, ruleId: 'best-selling-keywords' },
  },
  {
    id: 'category-keywords',
    priority: 5,
    condition: props => {
      const categoryTerms = /categor|wine.?type|varietal|region/i;
      return !!props.title && categoryTerms.test(props.title);
    },
    result: { section: 'categories', eventType: HOME_SECTION_BANNER, ruleId: 'category-keywords' },
  },
  {
    id: 'club-keywords',
    priority: 6,
    condition: props => {
      const clubTerms = /club|member|subscription|join/i;
      return !!props.title && clubTerms.test(props.title);
    },
    result: { section: 'clubs', eventType: HOME_SECTION_BANNER, ruleId: 'club-keywords' },
  },
  {
    id: 'deals-keywords',
    priority: 7,
    condition: props => {
      const dealTerms = /deal|sale|offer|discount|promo/i;
      return !!props.title && dealTerms.test(props.title);
    },
    result: { section: 'deals', eventType: HOME_SECTION_BANNER, ruleId: 'deals-keywords' },
  },
  {
    id: 'trending-keywords',
    priority: 8,
    condition: props => {
      const trendingTerms = /trend|new|latest|fresh|recent/i;
      return !!props.title && trendingTerms.test(props.title);
    },
    result: { section: 'trending', eventType: HOME_SECTION_BANNER, ruleId: 'trending-keywords' },
  },
  {
    id: 'unlimited-keywords',
    priority: 9,
    condition: props => {
      const unlimitedTerms = /unlimited|free.?ship|benefit/i;
      return !!props.title && unlimitedTerms.test(props.title);
    },
    result: { section: 'unlimited', eventType: HOME_SECTION_BANNER, ruleId: 'unlimited-keywords' },
  },
  {
    id: 'support-keywords',
    priority: 10,
    condition: props => {
      const supportTerms = /support|help|contact|service/i;
      return !!props.title && supportTerms.test(props.title);
    },
    result: { section: 'support', eventType: HOME_SECTION_BANNER, ruleId: 'support-keywords' },
  },
  {
    id: 'sms-keywords',
    priority: 11,
    condition: props => {
      const smsTerms = /sms|text|mobile|phone/i;
      return !!props.title && smsTerms.test(props.title);
    },
    result: { section: 'sms', eventType: HOME_SECTION_BANNER, ruleId: 'sms-keywords' },
  },
  {
    id: 'social-keywords',
    priority: 12,
    condition: props => {
      const socialTerms = /social|follow|facebook|twitter|instagram/i;
      return !!props.title && socialTerms.test(props.title);
    },
    result: { section: 'social', eventType: HOME_SECTION_BANNER, ruleId: 'social-keywords' },
  },
];

export class AnalyticsRulesEngine {
  private readonly rules: SectionRule[];

  constructor(rules: SectionRule[] = HOMEPAGE_RULES) {
    // Use slice().sort() to avoid mutating the input array and ensure compatibility
    this.rules = rules.slice().sort((a, b) => a.priority - b.priority);
  }

  analyzeContent(props: {
    title?: string;
    componentType?: string;
    layoutIndex?: number;
    contentItem?: any;
  }): SectionRule['result'] | null {
    for (const rule of this.rules) {
      if (rule.condition(props)) {
        return rule.result;
      }
    }
    return null;
  }

  getRuleById(ruleId: string): SectionRule | undefined {
    return this.rules.find(rule => rule.id === ruleId);
  }

  getAllRules(): SectionRule[] {
    return [...this.rules];
  }
}

export const homepageRulesEngine = new AnalyticsRulesEngine();
